(function () {
  const theme = window.WSTheme || {};
  const strings = theme.strings || {};
  const routes = theme.routes || {};
  const indexUrl = theme.momentIndex;
  const productCache = new Map();
  let indexPromise;

  if (!indexUrl) return;

  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  const formatMoney = (cents) => new Intl.NumberFormat(document.documentElement.lang === 'de' ? 'de-DE' : 'en-US', {
    style: 'currency',
    currency: window.Shopify?.currency?.active || 'EUR'
  }).format(Number(cents || 0) / 100);

  const productUrl = (handle) => `${routes.root || '/'}products/${encodeURIComponent(handle)}.js`;

  function cartHandles(cart) {
    return (cart.items || []).map((item) => item.handle || item.product_handle || String(item.url || '')
      .replace(/^.*\/products\//, '')
      .replace(/\?.*$/, '')).filter(Boolean);
  }

  async function loadIndex() {
    if (!indexPromise) {
      indexPromise = fetch(indexUrl, { headers: { Accept: 'application/json' } })
        .then((response) => response.ok ? response.json() : Promise.reject(new Error('Moment index unavailable.')))
        .then((index) => {
          if (index?.artifact_type !== 'wundersaam_storefront_moment_index' || index.public_data_only !== true || !Array.isArray(index.edges)) {
            throw new Error('Invalid moment index.');
          }
          return index;
        });
    }
    return indexPromise;
  }

  async function loadProduct(handle) {
    if (!productCache.has(handle)) {
      productCache.set(handle, fetch(productUrl(handle), { headers: { Accept: 'application/json' } })
        .then((response) => response.ok ? response.json() : null)
        .catch(() => null));
    }
    return productCache.get(handle);
  }

  function localizedReason(edge) {
    return strings.momentReasons?.[edge.reason_key] || edge.public_reason || '';
  }

  function cardMarkup(product, reason) {
    const productLink = product.url || `${routes.root || '/'}products/${encodeURIComponent(product.handle)}`;
    const image = product.featured_image
      ? `<img src="${escapeHtml(product.featured_image)}" alt="${escapeHtml(product.title)}" loading="lazy" width="480" height="600">`
      : '<span class="moment-card__placeholder" aria-hidden="true"></span>';
    const availability = product.available ? strings.momentAvailable : strings.momentSoldOut;
    return `<article class="moment-card">
      <a class="moment-card__image" href="${escapeHtml(productLink)}">${image}</a>
      <div class="moment-card__copy">
        <p class="moment-card__availability">${escapeHtml(availability)}</p>
        <h3><a href="${escapeHtml(productLink)}">${escapeHtml(product.title)}</a></h3>
        <p class="moment-card__price">${formatMoney(product.price)}</p>
        ${reason ? `<p class="moment-card__reason">${escapeHtml(reason)}</p>` : ''}
      </div>
    </article>`;
  }

  function selectionForCart(index, handles) {
    const inCart = new Set(handles);
    const candidates = index.edges
      .filter((edge) => edge.channel_fit.includes('cart') && inCart.has(edge.source_handle) && !inCart.has(edge.target_handle) && edge.source_handle !== edge.target_handle)
      .sort((left, right) => left.display_priority - right.display_priority);
    return {
      primary: candidates[0] || null,
      complete: candidates.length === 0 && index.edges.some((edge) => inCart.has(edge.source_handle) && inCart.has(edge.target_handle))
    };
  }

  async function renderPdp() {
    const slot = document.querySelector('[data-moment-pdp]');
    if (!slot) return;
    if (slot.dataset.momentRendered === 'true') return;
    try {
      const index = await loadIndex();
      const sourceHandle = slot.dataset.productHandle;
      const chain = (index.story_chains || []).find((item) => item.channel_fit.includes('pdp') && item.product_handles[0] === sourceHandle);
      const edges = index.edges
        .filter((edge) => edge.channel_fit.includes('pdp') && edge.source_handle === sourceHandle && edge.source_handle !== edge.target_handle)
        .sort((left, right) => left.display_priority - right.display_priority)
        .slice(0, 3);
      const targets = chain ? chain.product_handles.slice(1, 3) : edges.map((edge) => edge.target_handle);
      const products = await Promise.all(targets.map(loadProduct));
      const cards = products.map((product, indexPosition) => {
        if (!product) return '';
        const edge = edges.find((item) => item.target_handle === targets[indexPosition]);
        const reason = edge ? localizedReason(edge) : strings.momentStoryContinuation;
        return cardMarkup(product, reason);
      }).filter(Boolean);
      if (!cards.length) return;
      const title = chain ? strings.momentStoryHeading : strings.momentPdpHeading;
      const copy = chain ? strings.momentStoryCopy : strings.momentPdpCopy;
      slot.innerHTML = `<div class="moment-module__inner">
        <header class="moment-module__head"><p class="label-micro">${escapeHtml(strings.momentEyebrow)}</p><h2>${escapeHtml(title)}</h2>${copy ? `<p>${escapeHtml(copy)}</p>` : ''}</header>
        <div class="moment-module__cards moment-module__cards--${cards.length}">${cards.join('')}</div>
      </div>`;
      slot.hidden = false;
    } catch (error) {
      void error;
    }
  }

  async function renderHome() {
    const section = document.querySelector('[data-moment-home]');
    const slot = section?.querySelector('[data-moment-home-products]');
    if (!section || !slot || section.dataset.momentHomeSuppressed === 'true') return;
    try {
      const index = await loadIndex();
      const chain = (index.story_chains || []).find((item) => Array.isArray(item.product_handles) && item.product_handles.length >= 3);
      if (!chain) return;
      const handles = chain.product_handles.slice(0, 3);
      const products = await Promise.all(handles.map(loadProduct));
      const cards = products.map((product, position) => {
        if (!product) return '';
        const sourceHandle = position > 0 ? handles[position - 1] : handles[0];
        const edge = index.edges.find((item) => item.source_handle === sourceHandle && item.target_handle === handles[position]);
        const reason = edge ? localizedReason(edge) : (position === 0 ? strings.momentStoryCopy : strings.momentStoryContinuation);
        return cardMarkup(product, reason);
      }).filter(Boolean);
      if (cards.length !== handles.length) return;
      slot.innerHTML = `<div class="moment-module__cards moment-module__cards--3">${cards.join('')}</div>`;
      section.hidden = false;
    } catch (error) {
      void error;
    }
  }

  async function renderCart(cart) {
    const slot = document.querySelector('[data-moment-cart]');
    if (!slot) return;
    slot.innerHTML = '';
    slot.hidden = true;
    if (!cart?.items?.length) return;
    try {
      const index = await loadIndex();
      const selection = selectionForCart(index, cartHandles(cart));
      if (selection.primary) {
        const product = await loadProduct(selection.primary.target_handle);
        if (!product?.available) return;
        slot.innerHTML = `<div class="moment-cart__inner"><p class="label-micro">${escapeHtml(strings.momentEyebrow)}</p><h3>${escapeHtml(strings.momentCartHeading)}</h3>${cardMarkup(product, localizedReason(selection.primary))}</div>`;
        slot.hidden = false;
      } else if (selection.complete) {
        slot.innerHTML = `<p class="moment-cart__complete">${escapeHtml(strings.momentCartComplete)}</p>`;
        slot.hidden = false;
      } else {
        slot.innerHTML = '';
        slot.hidden = true;
      }
    } catch (error) {
      void error;
    }
  }

  document.addEventListener('ws:cart-rendered', (event) => renderCart(event.detail?.cart));
  renderPdp();
  renderHome();
  window.WSMomentRecommendations = { renderCart, renderPdp, renderHome };
})();
